/* ============================================================================
   HPN SCIENCE KALORIENRECHNER - Bricks JS-Feld (generiert von ui/build.js)
   NICHT direkt editieren. Quelle: engine/engine.js + ui/ui-sources.js + ui/ui-controller.js
   Reihenfolge: Engine (window.HPNCalc), dann Quellen, dann Controller.
   ============================================================================ */

/* ============================================================================
   HPN SCIENCE - KALORIENRECHNER - ENGINE (Neubau)
   Reine Rechenfunktionen, kein DOM (kein document, kein window-Event).
   Jede Konstante mit Quelle. Belegquellen: ../quellen/ (Reports + KaloriAI-Doc),
   plus im Code benannte Primaerstudien. Single Source of Truth: ../BERECHNUNGSLOGIK.md.

   Stand dieses Kerns (Schritte 1 bis 5 komplett, Audit-Fixes 2026-07-03):
   - BMR (4 Formeln, Profilwahl, Pontzer), NEAT additiv (keine PAL-Doppelzaehlung),
     EAT/EPOC, TEF, AT, Supplement, Muscle-Memory, Ziel, Makros, Navy-KFA, Caps.
   - Schritt 3: Alkohol als Budget-Abzug pro Woche. Schritt 4: adaptive
     Nachkalibrierung. Schritt 5: Schlaf als kleiner Faktor, Stress/Zyklus als Hinweis.
   Audit-Fixes 2026-07-03:
   - Carb-Cycling trainingstage-gewichtet (Wochenschnitt trifft das Budget exakt).
   - NEAT-Schrittpfad mit nicht-ambulatorischer Basis (kein Absturz unter den
     Sitz-Anker mehr, wenn Schritte eingetragen werden).
   - Schlaf-Abschlag VOR dem Plausibilitaets-Cap (Floor wird nie unterschritten).
   - EPOC: RPE wird auf 4 bis 10 gerundet und geklemmt.
   ============================================================================ */
(function (global) {
  'use strict';

  /* ---------- utils ------------------------------------------------------- */
  function round(n, d) { const f = Math.pow(10, d || 0); return Math.round(n * f) / f; }
  // Zahl-Guard: macht aus null/undefined/NaN/Text eine sichere Zahl (Default 0).
  function num(v, def) { const n = typeof v === 'number' ? v : parseFloat(v); return isFinite(n) ? n : (def == null ? 0 : def); }

  /* ---------- 1. BMR ------------------------------------------------------ */
  // Mifflin-St-Jeor (Allgemein-Fallback). [Mifflin 1990; quellen/Report Kap.1]
  function bmrMifflin({ kg, cm, age, sex }) {
    const base = 10 * kg + 6.25 * cm - 5 * age;
    return sex === 'f' ? base - 161 : base + 5;
  }
  // Oxford/Henry 2005 - beste Allgemeinformel (Groesse in Metern). [Henry 2005 PHN]
  function bmrOxford({ kg, cm, age, sex }) {
    const m = cm / 100;
    let v;
    if (sex === 'f') {
      if (age < 18) v = 9.40 * kg + 249 * m + 462;   // Henry 2005, 10-18 Jahre
      else if (age < 30) v = 10.4 * kg + 615 * m - 282;
      else if (age < 60) v = 8.18 * kg + 502 * m - 11.6;
      else v = 8.52 * kg + 421 * m + 10.7;
    } else {
      if (age < 18) v = 15.6 * kg + 266 * m + 299;   // Henry 2005, 10-18 Jahre
      else if (age < 30) v = 14.4 * kg + 313 * m + 113;
      else if (age < 60) v = 11.4 * kg + 541 * m - 137;
      else v = 11.4 * kg + 541 * m - 256;
    }
    return v;
  }
  // Cunningham 1991 / Katch-McArdle (KFA bekannt). [Report Kap.1; KaloriAI 2.1]
  function bmrKatch({ kg, bf }) {
    const lbm = kg * (1 - bf / 100);
    return 370 + 21.6 * lbm;
  }
  // Ten-Haaf 2014 - Athleten (FFM wenn KFA bekannt, sonst gewichtsbasiert). [ten Haaf 2014 PLOS ONE]
  function bmrTenHaaf({ kg, cm, age, sex, bf }) {
    if (bf != null) {
      const ffm = kg * (1 - bf / 100);
      return 22.771 * ffm + 484.264;
    }
    const m = cm / 100;
    return 11.936 * kg + 587.728 * m - 8.129 * age + 191.027 * (sex === 'm' ? 1 : 0) + 29.279;
  }
  // Pontzer-Alterskorrektur: BMR 20-60 stabil, erst >60 jaehrlich ca. 0,7 %. [Pontzer 2021 Science; Report Kap.10]
  function ageAdjust(bmr, age) {
    if (age <= 60) return 0;
    return -(0.007 * (age - 60) * bmr);
  }

  /**
   * Waehlt die beste BMR-Formel nach Kontext und gibt alle Varianten zurueck.
   * trained: true bei >= ca. 7h intensivem Training/Woche.
   * BMR-Schwelle: Warnung bei unter 800 kcal (KaloriAI-Doc), KEIN fabrizierter Hard-Floor
   *   (Entscheidung Sami 5.3). Nur harte Sicherung gegen nicht-physische Werte.
   */
  function computeBMR(p) {
    // Physische Groessen koennen nicht negativ sein -> abhaerten gegen Unsinnseingaben.
    const kg = Math.max(0, num(p.kg)), cm = Math.max(0, num(p.cm)), age = Math.max(0, num(p.age)), sex = p.sex === 'f' ? 'f' : 'm';
    // KFA nur im physiologischen Bereich 3-60 % (Pruefung 28.08.2026: KFA 150 ergab BMR 0).
    const bfRaw = (p.bf == null || p.bf === '' || !isFinite(parseFloat(p.bf))) ? null : parseFloat(p.bf);
    const bf = bfRaw == null ? null : Math.min(60, Math.max(3, bfRaw));
    const trained = !!p.trained;
    const variants = {};
    variants.mifflin = round(bmrMifflin({ kg, cm, age, sex }));
    variants.oxford = round(bmrOxford({ kg, cm, age, sex }));
    if (bf != null) {
      variants.katch = round(bmrKatch({ kg, bf }));
      variants.tenHaaf = round(bmrTenHaaf({ kg, cm, age, sex, bf }));
    } else {
      variants.tenHaaf = round(bmrTenHaaf({ kg, cm, age, sex, bf: null }));
    }

    let chosen, label, formula;
    if (bf != null) {
      if (trained) { chosen = variants.tenHaaf; label = 'Ten-Haaf (Athlet, FFM)'; formula = 'tenHaaf'; }
      else { chosen = variants.katch; label = 'Cunningham/Katch-McArdle (FFM)'; formula = 'katch'; }
    } else {
      if (trained) { chosen = variants.tenHaaf; label = 'Ten-Haaf (Athlet)'; formula = 'tenHaaf'; }
      else { chosen = variants.oxford; label = 'Oxford/Henry 2005'; formula = 'oxford'; }
    }
    // Pontzer-Korrektur nur auf Formeln ohne eigenen Altersterm (Katch). Oxford und
    // ten Haaf tragen das Alter bereits, sonst wuerde >60 doppelt abgezogen.
    const adj = formula === 'katch' ? round(ageAdjust(chosen, age)) : 0;
    let bmr = chosen + adj;
    const warnings = [];
    if (bfRaw != null && bfRaw !== bf) warnings.push('Der Körperfettanteil wurde auf ' + bf + ' % begrenzt (möglicher Bereich 3 bis 60 %). Bitte Eingabe prüfen.');
    if (!isFinite(bmr) || bmr <= 0) bmr = 0; // harte Sicherung, verhindert NaN/negativ
    else if (bmr < 800) warnings.push('Der berechnete Grundumsatz liegt unter 800 kcal. Das ist für Erwachsene sehr niedrig, bitte Eingaben (Gewicht, Größe, Körperfett) prüfen.');
    return { bmr: round(bmr), label, formula, variants, ageAdjust: adj, raw: round(chosen), warnings };
  }

  /* ---------- 2. NEAT (NEU: additiv, keine PAL-Doppelzaehlung) ------------ */
  // WICHTIG: NEAT enthaelt NIE das strukturierte Training (das kommt aus EAT).
  // Alte Modelle (v3/bricks) rechneten NEAT = BMR x (PAL-1). PAL = TDEE/BMR enthaelt
  // aber bereits Training + TEF -> Doppelzaehlung, und die Beruf-Spreizung wurde bis
  // ~1300 kcal (Alex-Beobachtung). Neu: absolute kcal-Anker aus der Evidenz,
  // mild mit dem Koerpergewicht skaliert (schwerer = teurer pro Bewegung).
  //
  // Anker bei 80 kg Referenz. Evidenz: sitzender Beruf NEAT ~300 kcal/Tag,
  // koerperliche Arbeit bis ~2000 kcal/Tag. [quellen/Report Kap.4]
  // Steh-vs-Sitz real nur ~0,15 kcal/min = ~72 kcal/8h. [Saeidifard 2018; PubMed 28719766]
  const REF_KG = 80;
  const JOB_NEAT_80 = {
    office:   300,   // Buero/Homeoffice, ueberwiegend sitzend (Report-Anker)
    mixed:    450,   // sitzend/stehend gemischt (Lehrer, Verkauf)
    standing: 550,   // stehender Beruf (Friseur, Einzelhandel): office + Steh-Bonus + etwas Gehen
    physical: 850,   // koerperliche Arbeit (Handwerk, Pflege, Lager)
    heavy:    1250,  // schwere koerperliche Arbeit (Bau, Forst)
    field:    1800   // nicht-mechanisierte Landwirtschaft (nahe Obergrenze ~2000)
  };
  // Fallback-Level ohne Berufsangabe, gleiche Anker-Logik.
  const ACT_NEAT_80 = {
    sedentary: 300, light: 450, moderate: 700, high: 1050, extreme: 1500
  };
  // Fidgeting / spontane Bewegungsunruhe (kcal/Tag). [Levine 1999 Science]
  const FIDGET = { low: 0, normal: 100, high: 250 };

  // Nicht-ambulatorische NEAT-Basis fuer den Schrittpfad (Audit-Fix 2026-07-03).
  // Die Schritt-Formel deckt nur das Gehen ab. Haltung, Haus- und Armarbeit stecken
  // in den Berufsankern. Basis = Berufsanker minus kcal der berufstypischen
  // Schrittzahl (bei 80 kg: Schritte x 80 x 0.0005). Typische Schrittzahlen nach
  // Berufskategorie: sitzend ~4500 (sedentaer = unter 5000 Schritte/Tag,
  // Tudor-Locke/Bassett 2004), Service/stehend ~7500 (Tudor-Locke 2011,
  // occupational step counts), koerperlich 11000+, schwer 13000+, Feld 16000.
  // Konsistenz: traegt jemand die berufstypische Schrittzahl ein, reproduziert der
  // Schrittpfad exakt den Berufsanker. Ohne Berufsangabe gilt die Sitz-Basis.
  const JOB_NONSTEP_80 = {
    office:   120,   // 300 - 4500 x 0.04
    mixed:    150,   // 450 - 7500 x 0.04
    standing: 250,   // 550 - 7500 x 0.04 (enthaelt berufstypisches Stehen)
    physical: 410,   // 850 - 11000 x 0.04 (Heben, Tragen, Armarbeit)
    heavy:    730,   // 1250 - 13000 x 0.04
    field:    1160   // 1800 - 16000 x 0.04
  };

  // Schrittbasiert (bevorzugt, sauber, kein Doppelzaehlen). [KaloriAI 2.2 / BiteKit]
  function neatFromSteps(steps, kg) { return steps * kg * 0.0005; }
  // Stehstunden-Bonus vs. Sitzen: +0,15 kcal/min. [Saeidifard 2018]
  // Hinweis: bei Berufen, deren Basis Stehen schon enthaelt (standing aufwaerts),
  // nur zusaetzliches Stehen ausserhalb der Arbeit eintragen (UI-Text regelt das).
  function neatStanding(hours) { return num(hours) * 60 * 0.15; }

  function computeNEAT(inp) {
    const kg = num(inp.kg, REF_KG) || REF_KG;
    const steps = num(inp.steps);
    const fidgetKcal = FIDGET[inp.fidget] != null ? FIDGET[inp.fidget] : 0;
    let kcal, source;
    if (steps > 0) {
      const base80 = (inp.job && JOB_NONSTEP_80[inp.job] != null) ? JOB_NONSTEP_80[inp.job] : JOB_NONSTEP_80.office;
      const nonStep = base80 * (kg / REF_KG);
      kcal = nonStep + neatFromSteps(steps, kg) + neatStanding(inp.standingHours) + fidgetKcal;
      source = 'Schritte + Grundaktivität' + (num(inp.standingHours) ? ' + Stehstunden' : '') + (fidgetKcal ? ' + Bewegungsunruhe' : '');
    } else if (inp.job && JOB_NEAT_80[inp.job] != null) {
      kcal = JOB_NEAT_80[inp.job] * (kg / REF_KG) + neatStanding(inp.standingHours) + fidgetKcal;
      source = 'Beruf/Alltag' + (num(inp.standingHours) ? ' + Stehstunden' : '') + (fidgetKcal ? ' + Bewegungsunruhe' : '');
    } else {
      const key = ACT_NEAT_80[inp.level] != null ? inp.level : 'sedentary';
      kcal = ACT_NEAT_80[key] * (kg / REF_KG) + neatStanding(inp.standingHours) + fidgetKcal;
      source = 'Aktivitätslevel' + (num(inp.standingHours) ? ' + Stehstunden' : '') + (fidgetKcal ? ' + Bewegungsunruhe' : '');
    }
    return { kcal: round(Math.max(0, kcal)), source, fidget: fidgetKcal };
  }

  /* ---------- 3. EAT (Multi-Block, MET 2024 Compendium) ------------------- */
  // [2024 Adult Compendium of Physical Activities; quellen/KaloriAI 2.3]
  const MET = {
    strength: { met: 5.0, label: 'Krafttraining' },
    strength_vigorous: { met: 6.0, label: 'Krafttraining intensiv / Powerlifting' },
    cardio_light: { met: 3.0, label: 'Ausdauer leicht (Gehen, Yoga)' },
    cycling: { met: 6.8, label: 'Radfahren moderat (15–20 km/h)' },
    cycling_intense: { met: 10.0, label: 'Radfahren zügig (über 22 km/h)' },
    swimming: { met: 6.0, label: 'Schwimmen moderat' },
    running_8: { met: 8.3, label: 'Laufen 8 km/h' },
    running_10: { met: 10.0, label: 'Laufen 10 km/h' },
    hiit: { met: 10.0, label: 'HIIT' },
    football_game: { met: 8.5, label: 'Fußball (Spiel)' },
    football_train: { met: 6.0, label: 'Fußball (Training)' },
    rowing: { met: 7.0, label: 'Rudern Ergometer' },
    jumping: { met: 10.0, label: 'Seilspringen' },
    custom: { met: 5.0, label: 'Sonstiges' }
  };
  // EPOC-Faktor nach RPE: 5-20 % des Trainings. [quellen/Report Kap.3]
  const EPOC_FACTOR = { 4: 0.05, 5: 0.05, 6: 0.10, 7: 0.10, 8: 0.15, 9: 0.15, 10: 0.20 };

  // Ein Block: kcal fuer die Session inkl. EPOC. trained -> +5 % MET-Korrektur.
  // [Individualisierte 1-MET-Werte, PubMed 6412759; KaloriAI 2.3 Hinweis]
  // FLAG (ANALYSE 2 Nr.7): MET Kraft 5.0 ergibt ~7 kcal/min bei 80 kg, Report-Mittel ~6.
  //   Bewusst beibehalten (im Bereich 4-8), spaeter ggf. kalibrieren.
  function eatBlock(b, kg, trained) {
    const type = b.type || 'custom';
    const mBase = (type === 'custom' && num(b.met)) ? num(b.met) : (MET[type] ? MET[type].met : 5.0);
    const corr = trained ? 1.05 : 1.0;
    const duration = Math.max(0, num(b.duration));
    // RPE auf 4-10 runden und klemmen (Audit-Fix 2026-07-03): vorher fielen RPE
    // unter 4 und halbe RPE (z.B. 7.5) auf den 0.10-Fallback statt auf die Tabelle.
    const rpe = Math.min(10, Math.max(4, Math.round(num(b.rpe, 7))));
    // Netto-MET (MET - 1): der Ruheumsatz waehrend des Trainings steckt schon im BMR.
    const eat = (Math.max(0, mBase * corr - 1) * kg * 3.5 / 200) * duration;
    const epoc = eat * (EPOC_FACTOR[rpe] || 0.10);
    return { eat: round(eat), epoc: round(epoc), total: round(eat + epoc), met: round(mBase * corr, 2) };
  }

  /* ---------- 4. TEF ------------------------------------------------------ */
  // Makro-basiert (praezise): P 25 %, C 8 %, F 2 %. [quellen/Report Kap.5; KaloriAI 2.5]
  function tefFromMacros({ protein, carbs, fat }) {
    return num(protein) * 4 * 0.25 + num(carbs) * 4 * 0.08 + num(fat) * 9 * 0.02;
  }
  // Fallback: %-Schaetzer nach Proteinstatus.
  const TEF_PCT = { low: 0.07, normal: 0.10, high: 0.13, veryhigh: 0.17 };
  function tefFromStatus(energySoFar, status) { return num(energySoFar) * (TEF_PCT[status] || 0.10); }

  /* ---------- 5. Adaptive Thermogenese ----------------------------------- */
  // kg-Verlust in 4 Wochen -> kcal-Reduktion, Cap -430. [PLOS ONE -431 nach 3 Mon; Report Kap.9]
  function adaptiveThermo(kgLoss4wk) {
    const v = num(kgLoss4wk);
    if (v <= 0) return 0;
    return -Math.min(430, v * 86);
  }

  /* ---------- 5a. Schlaf (kleiner, gedeckelter Faktor) ------------------- */
  // Chronischer Schlafmangel senkt die spontane Alltagsaktivitaet (NEAT) und damit
  // den Verbrauch leicht. Bewusst klein und gedeckelt gehalten (keine Scheinpraezision).
  // [Schlafrestriktion reduziert Aktivitaet/NEAT; Effekt moderat]
  // Gibt einen Faktor auf den TDEE zurueck (negativ = weniger Verbrauch).
  function sleepFactor(hours) {
    const h = num(hours);
    if (h <= 0) return 0;      // keine Angabe -> keine Korrektur
    if (h < 5) return -0.03;   // schwerer Mangel
    if (h < 6) return -0.015;  // leichter Mangel
    return 0;                  // 6 h und mehr: normal, keine Korrektur
  }

  /* ---------- 5b. Alkohol (Budget-Abzug, NICHT TDEE) --------------------- */
  // Entscheidung Sami: Eingabe pro Woche, Alkohol ist Energie-AUFNAHME, kein Verbrauch.
  // Er wird daher NICHT zum TDEE addiert, sondern vom Essensbudget abgezogen.
  // Netto-Energie: Ethanol 7.1 kcal/g minus ca. 20 % obligatorische Thermogenese (TEF
  // Alkohol) = ca. 5.7 kcal/g. [Suter 1994; quellen/Report Kap.5]
  // 1 Standardglas = ca. 12 g reiner Alkohol (BZgA-Groessenordnung 10-12 g).
  const ALC_KCAL_PER_G = 5.7;
  const ALC_GRAMS_PER_DRINK = 12;
  function alcoholBudget(drinksPerWeek, gramsPerDrink) {
    const drinks = Math.max(0, num(drinksPerWeek));
    const gpd = num(gramsPerDrink, ALC_GRAMS_PER_DRINK) || ALC_GRAMS_PER_DRINK;
    const gramsPerWeek = drinks * gpd;
    const kcalPerWeek = gramsPerWeek * ALC_KCAL_PER_G;
    return {
      drinksPerWeek: drinks,
      gramsPerWeek: round(gramsPerWeek),
      gramsPerDay: round(gramsPerWeek / 7, 1),
      kcalPerWeek: round(kcalPerWeek),
      kcalPerDay: round(kcalPerWeek / 7)
    };
  }

  /* ---------- 6. Supplement-Thermogenese (HPN) --------------------------- */
  // Koffein-Saettigungskurve + Habituation. [Astrup 1990; Report Kap.6]
  // Liefert kcal/Tag bei gegebener Tagesdosis (alle Quellen), KEINE Doppelzaehlung.
  function caffeineKcal(mgTotal, habituation, bmr) {
    const mg = num(mgTotal);
    if (mg < 25) return 0;
    const eff = Math.min(mg, 500);                       // Plateau ~500 mg
    const pct = Math.max(0, 3.5 + 1.5 * Math.log(eff / 100)); // % RMR
    const hours = Math.min(eff / 100 * 2.5, 16);
    const hab = habituation === 'daily' ? 0.5 : habituation === 'occasional' ? 0.75 : 1.0;
    const kcal = (pct / 100) * (num(bmr) / 24) * hours * hab;
    return round(Math.max(0, kcal));
  }

  // Integriert Ignite + Heat + Alltags-Koffein OHNE Doppelzaehlung.
  // KaloriAI-Doc (Heat +180 fix) gilt als veraltet; Wahrheit ist diese dynamische
  // Logik (Entscheidung Sami 5.2). Ehrlichkeit: kcal beruhen auf Einzel-Inhaltsstoffen,
  // nicht auf einer klinischen Studie zur Gesamtformel (AGENTS.md).
  function supplementThermo({ ignite, heat, extraCaffeineMg, habituation, bmr }) {
    const HEAT_CAFFEINE = 200;     // mg [Report Kap.12]
    const HEAT_SYNEPHRINE = 18;    // mg/Tag (EU-Grenze 21) [DB]
    const breakdown = [];
    const warnings = [];

    const baseMg = num(extraCaffeineMg);
    const totalMg = baseMg + (heat ? HEAT_CAFFEINE : 0);
    const baseKcal = baseMg > 0 ? caffeineKcal(baseMg, habituation, bmr) : 0;      // Alltags-Koffein (zaehlt immer zum TDEE)
    const totalKcal = totalMg > 0 ? caffeineKcal(totalMg, habituation, bmr) : 0;
    const heatCaffKcal = round(totalKcal - baseKcal);                             // Zusatz nur durch Heat-Koffein

    let heatNonStim = 0, igniteKcal = 0, overlap = 0;
    if (heat) {
      heatNonStim = 90; // Paradol 37.5mg, Capsaicin, Synephrin, Theobromin, konservativ [Report Kap.12]
      if (heatCaffKcal > 0) breakdown.push({ key: 'heatcaff', label: 'Heat: Koffein-Anteil (verrechnet)', kcal: heatCaffKcal });
      breakdown.push({ key: 'heat', label: 'Heat: Wirkstoffe (Paradol, Capsaicin u.ä.)', kcal: heatNonStim });
    }
    if (ignite) {
      igniteKcal = 100; // EGCG 135 + Paradol 25 + Capsaicin + Fucoxanthin [Report Kap.11]
      breakdown.push({ key: 'ignite', label: 'Ignite (stimulanzienfrei)', kcal: igniteKcal });
    }
    if (ignite && heat) {
      overlap = -40; // gemeinsame Wirkstoffe -> Kombi konservativ [Report]
      breakdown.push({ key: 'overlap', label: 'Überlappungs-Abschlag (Kombi)', kcal: overlap });
    }

    if (heat) warnings.push('Heat enthält ' + HEAT_SYNEPHRINE + ' mg Synephrin/Tag (EU-Höchstmenge 21 mg aus allen Quellen). Nicht mit weiteren Synephrin-Produkten kombinieren.');
    if (totalMg > 400) warnings.push('Deine Koffein-Gesamtdosis liegt über 400 mg/Tag (EFSA-Tagesempfehlung). Der thermogene Effekt ist bereits gedeckelt.');
    if (totalMg > 300 && heat) warnings.push('Hohe Koffeindosis: Heat nicht abends einnehmen, um den Schlaf nicht zu stören.');

    const hpnKcal = round(heatCaffKcal + heatNonStim + igniteKcal + overlap);
    return {
      kcal: hpnKcal,
      baseCaffeineKcal: round(baseKcal),
      breakdown, caffeineTotalMg: totalMg, warnings
    };
  }

  /* ---------- 7. Muscle Memory ------------------------------------------- */
  // Ueberschuss-Anpassung fuer Lean Bulk. [epigenetisches Memory, Report Kap.8]
  const MM = {
    never:   { surplus: 200, note: '', recomp: false },
    // Unregelmaessig/immer mal wieder: kein nutzbares Muskelgedaechtnis, daher wie 'never'. [Report Kap.8]
    irregular:{ surplus: 200, note: '', recomp: false },
    active:  { surplus: 175, note: 'Aktiv - kein Abschlag nötig.', recomp: false },
    weak:    { surplus: 175, note: 'Pause 3-18 Monate: Muskelgedächtnis aktiv.', recomp: false },
    strong:  { surplus: 150, note: 'Pause 2-5 Jahre: starkes Muskelgedächtnis - Recomp möglich.', recomp: true },
    moderate:{ surplus: 175, note: 'Pause über 5 Jahre: Gedächtnis abgeschwächt, Recomp bedingt.', recomp: false }
  };

  /* ---------- 8. Plausibilitaet ------------------------------------------ */
  // TDEE zwischen 1.2x und 2.5x BMR. [2.5x BMR nachhaltiges Limit, Report Kap.10]
  function plausibility(tdee, bmr) {
    const warnings = [];
    let final = num(tdee);
    const b = num(bmr);
    if (b > 0 && final > 2.5 * b) {
      warnings.push('Der berechnete Verbrauch liegt über dem nachhaltigen Limit (~2,5x Grundumsatz). Bitte Trainings-/Aktivitätsangaben prüfen.');
      final = 2.5 * b;
    }
    if (b > 0 && final < 1.2 * b) {
      warnings.push('Der Wert liegt unter einem gesunden Minimum (1,2x Grundumsatz).');
      final = Math.max(final, 1.2 * b);
    }
    return { tdee: round(final), warnings };
  }

  /* ---------- 9. Ziel -> Kalorien ---------------------------------------- */
  function targetCalories(tdee, goal, mm, opt) {
    const t = num(tdee);
    const m = MM[mm] || MM.never;
    let target, deficit = 0, surplus = 0, note = '';
    switch (goal) {
      case 'loss': {
        const pct = opt && opt.lossPct ? opt.lossPct : 0.20; // 0.10 sanft / 0.15 moderat / 0.20 aggressiv
        deficit = Math.min(Math.round(t * pct), Math.round(t * 0.25));
        target = t - deficit;
        note = 'Defizit ' + Math.round(pct * 100) + '% (max. 25%).';
        break;
      }
      case 'hold': target = t; note = 'Erhaltung.'; break;
      case 'muscle': surplus = m.surplus; target = t + surplus; note = 'Lean Bulk +' + surplus + ' kcal.'; break;
      case 'bulk': surplus = (mm === 'strong') ? 250 : 350; target = t + surplus; note = 'Massephase +' + surplus + ' kcal.'; break;
      case 'recomp': target = m.recomp ? t : t - 100; note = m.recomp ? 'Recomp auf Erhaltung.' : 'Recomp leichtes Defizit -100 kcal.'; break;
      default: target = t;
    }
    return { target: round(target), deficit, surplus, note };
  }

  // KFA-angepasste Energiedichte des Gewebes. [Hall 2007]
  function tissueEnergyDensity(bf, sex) {
    if (bf == null) return 7000;
    const high = sex === 'f' ? 35 : 30;
    const low = sex === 'f' ? 22 : 15;
    if (bf >= high) return 7700;
    if (bf <= low) return 5800;
    return round(5800 + (7700 - 5800) * (bf - low) / (high - low));
  }
  function weeklyChange(target, tdee, bf, sex) {
    const dens = tissueEnergyDensity(bf, sex);
    if (!dens) return 0;
    return round((num(target) - num(tdee)) * 7 / dens, 2); // kg/Woche
  }

  /* ---------- 9b. Adaptive Nachkalibrierung (Kern-Hebel) ----------------- */
  // Rechnet den WAHREN Verbrauch rueckwaerts aus echtem Gewichtsverlauf vs. Intake,
  // analog zum MacroFactor-Prinzip, nur offline und manuell. Ueberschreibt die
  // Formel-Schaetzung, weil gemessen > geschaetzt.
  // impliedTDEE = avgIntake - (Gewichtsdifferenz_kg x Energiedichte) / Tage.
  // Beispiel: 2500 kcal/Tag gegessen, in 28 Tagen 1 kg verloren (dens 7000):
  //   2500 - (-1 x 7000)/28 = 2500 + 250 = 2750 kcal wahrer Verbrauch.
  // [Energiebilanz-Prinzip; Dichte aus Hall 2007; MacroFactor-Methodik als Vorbild]
  function adaptiveCalibration(c) {
    c = c || {};
    const start = num(c.startWeightKg), cur = num(c.currentWeightKg);
    const intake = num(c.avgIntakeKcal), d = num(c.days);
    // Wochenschnitt-Modus: Endpunkte sind 7-Tage-Durchschnittsgewichte statt einzelner
    // Wiegetage. Daempft das Tagesrauschen (Wasser, Glykogen, Salz, Zyklus) um ~Faktor
    // Wurzel aus 7 und ist der Standard der Trend-Gewicht-Methodik (MacroFactor-Prinzip).
    const weekly = c.weeklyAverages === true;
    const minDays = weekly ? 14 : 7; // zwei nicht ueberlappende 7-Tage-Fenster noetig
    if (!(intake > 0) || !(d >= minDays) || !(start > 0) || !(cur > 0)) {
      return { ok: false, note: weekly
        ? 'Für die Nachkalibrierung brauchen wir dein Durchschnittsgewicht der ersten und der letzten Woche, die durchschnittlich gegessenen Kalorien und mindestens 14 Tage Zeitraum (21 bis 28 Tage sind deutlich genauer).'
        : 'Für die Nachkalibrierung brauchen wir Startgewicht, aktuelles Gewicht, die durchschnittlich gegessenen Kalorien und mindestens 7 Tage Zeitraum (14 bis 28 Tage sind deutlich genauer).', warnings: [] };
    }
    const dens = tissueEnergyDensity(c.bf == null ? null : c.bf, c.sex); // kcal/kg
    const deltaKg = cur - start;
    // Wirksamer Zeitraum bei Wochenschnitten: Abstand der Fenster-Mittelpunkte
    // (Mitte Woche 1 bis Mitte letzte Woche) = Gesamttage minus 7. Ohne diese
    // Korrektur wuerde die Aenderungsrate systematisch unterschaetzt.
    const effDays = weekly ? (d - 7) : d;
    let implied = intake - (deltaKg * dens) / effDays;
    const warnings = [];
    if (!weekly && d < 14) warnings.push('Weniger als 14 Tage Zeitraum: die Nachkalibrierung ist noch unsicher. 3 bis 4 Wochen gleichmäßiges Tracking machen sie deutlich genauer.');
    if (weekly && d < 21) warnings.push('Weniger als 3 Wochen Zeitraum: die Wochenfenster liegen noch dicht beieinander. 3 bis 4 Wochen Tracking machen die Nachkalibrierung deutlich genauer.');
    const bmr = num(c.bmr);
    if (bmr > 0 && implied > 2.7 * bmr) warnings.push('Der aus deinen Daten berechnete Verbrauch ist ungewöhnlich hoch. Prüfe, ob die Kalorien realistisch getrackt wurden.');
    if (bmr > 0 && implied < 1.1 * bmr) warnings.push('Der aus deinen Daten berechnete Verbrauch ist ungewöhnlich niedrig. Prüfe Kalorien-Tracking und Gewichtsmessung (Wasser schwankt stark).');
    if (implied < 0) implied = 0;
    return { ok: true, impliedTDEE: round(implied), deltaKg: round(deltaKg, 2), days: d, effectiveDays: effDays, weeklyAverages: weekly, kcalPerKg: dens, warnings };
  }

  // Makros: Protein nach Trainingsstatus/Ziel/Koerperbau, Fett nach kg (min 0.5),
  // Carbs aus Rest. Carb-Cycling: Trainingstag +35 %, Ruhetag so gewichtet, dass der
  // WOCHENSCHNITT exakt das Essensbudget trifft (Audit-Fix 2026-07-03).
  // trainingDays: 0-7. Bei 0 oder 7 gibt es keine Zyklus-Aufteilung (Train = Rest).
  //
  // PROTEIN (Neuaufsetzung 2026-07-03, Entscheidung Sami: harter Cap 2.0 g/kg, keine
  // Bodybuilder-Werte, Unterschied sportlich/nicht-sportlich und Mann/Frau):
  // g/kg Koerpergewicht nach Trainingsstatus und Ziel.
  //   Nicht-Sportler: RDA 0.8, gesundheitsoptimal 1.0-1.2, im Defizit etwas mehr
  //     zum Muskelerhalt und fuer die Saettigung. [WHO/DGE RDA; ESPEN 1.0-1.2 g/kg]
  //   Sportler: 1.4-2.0 g/kg genuegen fuer die meisten [ISSN Position Stand, Jaeger 2017];
  //     1.6 g/kg maximiert den Muskelaufbau [Morton 2018 Meta, PMC5867436];
  //     im Defizit oberes Ende zum Muskelerhalt [Helms 2014, PMID 24092765].
  // Mann/Frau: Protein zielt auf die fettfreie Masse. Ist der KFA bekannt, rechnen wir
  //   auf die fettfreie Masse (Frauen tragen mehr essentielles Fett -> real weniger
  //   absolutes Protein). Ohne KFA kleiner Frauen-Abschlag als Proxy. In JEDEM Fall
  //   harter Cap 2.0 g/kg Koerpergewicht.
  const P_TRAINED   = { loss: 2.0, hold: 1.6, muscle: 1.8, bulk: 1.6, recomp: 1.9 };
  const P_UNTRAINED = { loss: 1.5, hold: 1.0, muscle: 1.4, bulk: 1.2, recomp: 1.5 };
  const PROTEIN_CAP_GKG = 2.0; // harter Cap [Sami-Entscheidung; oberes Ende ISSN-Range]

  function proteinGrams({ kg, cm, goal, trains, sex, bf }) {
    const k = Math.max(0, num(kg));
    const cmForProtein = Math.max(0, num(cm));
    if (k <= 0) return 0;
    const table = trains ? P_TRAINED : P_UNTRAINED;
    const perKg = table[goal] != null ? table[goal] : (trains ? 1.6 : 1.0);
    // Bezugsmasse: fettfreie Masse (auf ~20% KFA-Referenz normiert, damit Schlanke
    // etwa beim Koerpergewicht landen), sonst Koerpergewicht mit Frauen-Proxy.
    let refKg;
    if (bf != null && isFinite(bf)) {
      const ffm = k * (1 - Math.min(60, Math.max(3, bf)) / 100);
      refKg = ffm / 0.80;
    } else {
      refKg = k * (sex === 'f' ? 0.90 : 1.0);
      // Ohne KFA bei BMI > 30: Bezug auf ein Gewicht bei BMI 27,5 statt aufs
      // Koerpergewicht, sonst entstehen unrealistische Proteinmengen.
      if (cmForProtein > 0) {
        const h2 = Math.pow(cmForProtein / 100, 2);
        if (k / h2 > 30) refKg = Math.min(refKg, 27.5 * h2 * (sex === 'f' ? 0.90 : 1.0));
      }
    }
    let g = perKg * refKg;
    const cap = PROTEIN_CAP_GKG * k;   // nie ueber 2.0 g/kg Koerpergewicht
    if (g > cap) g = cap;
    return round(g);
  }

  function macros({ target, kg, cm, goal, trainingDays, trains, sex, bf }) {
    const F = { loss: 0.8, hold: 1.0, muscle: 1.0, bulk: 1.0, recomp: 0.9 };
    const k = Math.max(0, num(kg));
    const protein_g = proteinGrams({ kg: k, cm, goal, trains: !!trains, sex, bf: (bf == null ? null : bf) });
    const fat_g = round(k * (F[goal] || 1.0));
    let carbs_kcal = num(target) - protein_g * 4 - fat_g * 9;
    if (carbs_kcal < 0) carbs_kcal = 0;
    const carbs_g = round(carbs_kcal / 4);
    const tD = Math.min(7, Math.max(0, Math.round(num(trainingDays, 0))));
    let carbsTrain, carbsRest;
    if (tD <= 0 || tD >= 7) {
      // Kein Zyklus moeglich (nur eine Tagesart) -> flach.
      carbsTrain = carbs_g; carbsRest = carbs_g;
    } else {
      // +35 % am Trainingstag, aber nie so viel, dass der Ruhetag negativ wuerde.
      const boosted = Math.min(carbs_g * 1.35, carbs_g * 7 / tD);
      carbsTrain = round(boosted);
      // Ruhetag = Rest des Wochenbudgets, gleichmaessig auf die Ruhetage verteilt.
      carbsRest = round(Math.max(0, (carbs_g * 7 - carbsTrain * tD) / (7 - tD)));
    }
    return {
      protein_g, fat_g, carbs_g,
      // tatsaechliche g/kg Koerpergewicht zur ehrlichen Anzeige (nach Cap/FFM-Bezug)
      protein_gkg: k > 0 ? round(protein_g / k, 1) : 0, fat_gkg: round(F[goal] || 1.0, 1),
      carbsTrain, carbsRest,
      check: round(protein_g * 4 + fat_g * 9 + carbs_g * 4)
    };
  }

  /* ---------- 10. US-Navy KFA-Schaetzung --------------------------------- */
  // Masse in cm, metrische Hodgdon-Beckett/US-Navy-Formel. [US Navy]
  function navyBodyFat({ sex, height, neck, waist, hip }) {
    const log10 = Math.log10 || (x => Math.log(x) / Math.LN10);
    const h = num(height), n = num(neck), w = num(waist), hp = num(hip);
    let bf;
    if (sex === 'f') {
      if (!hp) return null;
      const d = w + hp - n;
      if (d <= 0) return null;
      bf = 495 / (1.29579 - 0.35004 * log10(d) + 0.22100 * log10(h)) - 450;
    } else {
      const d = w - n;
      if (d <= 0) return null;
      bf = 495 / (1.0324 - 0.19077 * log10(d) + 0.15456 * log10(h)) - 450;
    }
    if (!isFinite(bf) || bf <= 0) return null;
    return round(Math.max(3, Math.min(60, bf)), 1);
  }

  /* ---------- MASTER: alles zusammen ------------------------------------- */
  function calculate(input) {
    input = input || {};
    // Physische Groessen abhaerten: negative Werte sind Unsinn -> auf 0 klemmen.
    const kg = Math.max(0, num(input.kg)), cm = Math.max(0, num(input.cm)), age = Math.max(0, num(input.age));
    const sex = input.sex === 'f' ? 'f' : 'm';
    const bf = (input.bf == null || input.bf === '' || !isFinite(parseFloat(input.bf))) ? null : parseFloat(input.bf);
    const trained = !!input.trained;

    // BMR
    const BMRres = computeBMR({ kg, cm, age, sex, bf, trained });
    const bmr = BMRres.bmr;

    // NEAT (unabhaengig vom BMR -> keine PAL-Doppelzaehlung)
    const NEATres = computeNEAT({ steps: input.steps, job: input.job, level: input.activityLevel, kg, fidget: input.fidget, standingHours: input.standingHours });

    // EAT: jeder Block hat perWeek Sessions. Wochen-Summe -> Tagesschnitt.
    let eatWeek = 0, epocWeek = 0, maxSessions = 0;
    const blockDetails = [];
    (input.blocks || []).forEach(b => {
      const r = eatBlock(b, kg, trained);
      const pw = Math.max(0, num(b.perWeek, 1));
      eatWeek += r.eat * pw; epocWeek += r.epoc * pw;
      if (pw > maxSessions) maxSessions = pw;
      blockDetails.push(Object.assign({ label: (b.type === 'custom' ? (b.label || 'Sonstiges') : (MET[b.type] ? MET[b.type].label : 'Sonstiges')), duration: num(b.duration), rpe: num(b.rpe, 7), perWeek: pw }, r));
    });
    const tDays = Math.min(7, Math.round(maxSessions));
    const restDays = 7 - tDays;
    const eatTrainDay = tDays > 0 ? eatWeek / tDays : 0;
    const epocTrainDay = tDays > 0 ? epocWeek / tDays : 0;

    function dayEnergy(eat, epoc) { return bmr + NEATres.kcal + eat + epoc; }
    const baseTrain = dayEnergy(eatTrainDay, epocTrainDay);
    const baseRest = dayEnergy(0, 0);

    // TEF (Makro-Direkteingabe oder Proteinstatus)
    const macroInput = input.macroInput;
    let tefTrain, tefRest, tefSource;
    if (macroInput && (num(macroInput.protein) || num(macroInput.carbs) || num(macroInput.fat))) {
      const t = round(tefFromMacros(macroInput));
      tefTrain = t; tefRest = t; tefSource = 'Makro-basiert (genaue Eingabe)';
    } else {
      tefTrain = round(tefFromStatus(baseTrain, input.proteinStatus || 'normal'));
      tefRest = round(tefFromStatus(baseRest, input.proteinStatus || 'normal'));
      tefSource = 'Proteinstatus: ' + (input.proteinStatus || 'normal');
    }

    // Adaptive Thermogenese
    const at = adaptiveThermo(input.weightLoss4wk || 0);

    // Supplements (HPN-Bonus) + Alltags-Koffein (separat)
    const supp = supplementThermo({ ignite: input.ignite, heat: input.heat, extraCaffeineMg: input.extraCaffeineMg, habituation: input.caffeineHabituation, bmr });
    const baseCaff = supp.baseCaffeineKcal || 0;

    // TDEE Trainings-/Ruhetag
    const tdeeTrain = baseTrain + tefTrain + at + baseCaff + supp.kcal;
    const tdeeRest = baseRest + tefRest + at + baseCaff + supp.kcal;
    const weeklyAvg = (tdeeTrain * tDays + tdeeRest * restDays) / 7;

    // Schlaf: kleiner, gedeckelter Abschlag auf die Schaetzung (chronischer Mangel).
    // Audit-Fix 2026-07-03: Schlaf wird VOR der Plausibilitaetspruefung angewandt,
    // damit der 1.2x-BMR-Floor als letzte Instanz nie unterschritten werden kann.
    const sleepF = sleepFactor(input.sleepHours);
    const sleepAdj = round(weeklyAvg * sleepF);

    // Plausibilitaet auf den schlaf-korrigierten Wochenschnitt (letzte Instanz)
    const plaus = plausibility(weeklyAvg + sleepAdj, bmr);
    const tdee = plaus.tdee; // korrigierte, gecappte Schaetzung

    // Adaptive Nachkalibrierung: wenn echte Tracking-Daten vorliegen, ueberschreibt der
    // gemessene Verbrauch die Schaetzung. Die Schaetzung (tdee) bleibt zur Transparenz sichtbar.
    // Hinweis: der kalibrierte Wert enthaelt bereits alles, was die Person im Zeitraum real
    // getan hat (Aktivitaet, Schlaf, ggf. Supplemente) -> keine zusaetzliche Schlaf-Korrektur.
    const calib = input.calibration ? adaptiveCalibration(Object.assign({ bf, sex, bmr }, input.calibration)) : { ok: false };
    const effectiveTDEE = calib.ok ? calib.impliedTDEE : tdee;
    const tdeeSource = calib.ok ? 'kalibriert (aus echtem Gewichtsverlauf)' : 'geschätzt (aus Eingaben)';

    // Ziel (auf dem effektiven, also wenn moeglich kalibrierten Verbrauch)
    const tgt = targetCalories(effectiveTDEE, input.goal || 'hold', input.muscleMemory || 'never', input.goalOpt);
    const wk = weeklyChange(tgt.target, effectiveTDEE, bf, sex);

    // Alkohol: Abzug vom Essensbudget, NICHT vom TDEE. Makros auf dem Essensbudget.
    const alc = alcoholBudget(input.alcoholPerWeek, input.gramsPerDrink);
    // Essensbudget nie unter den Grundumsatz (Pruefung 28.08.2026): sonst rutscht
    // das Ziel nach Alkoholabzug oder bei aggressivem Defizit unter den BMR.
    const foodRaw = Math.max(0, tgt.target - alc.kcalPerDay);
    const foodFloored = bmr > 0 && foodRaw < bmr;
    const foodTarget = foodFloored ? round(bmr) : foodRaw;
    // trains = macht ueberhaupt strukturiertes Training (nicht der strengere Athlet-Flag).
    // Steuert die Protein-Empfehlung (sportlich vs nicht-sportlich).
    const trains = tDays > 0;
    const mac = macros({ target: foodTarget, kg, cm, goal: input.goal || 'hold', trainingDays: tDays, trains, sex, bf });

    const bmi = cm > 0 ? kg / Math.pow(cm / 100, 2) : 0;
    const infoNotes = [];
    if (bmi > 35) infoNotes.push('Bei einem BMI über 35 werden alle Formeln ungenauer (bis ca. 20 % Abweichung). Ein bekannter Körperfettanteil erhöht die Genauigkeit. Ergebnis nach 3-4 Wochen mit echtem Tracking kalibrieren.');
    // Schlaf-Hinweis (nur wenn nicht kalibriert, sonst schon im Messwert enthalten)
    if (!calib.ok && sleepAdj < 0) infoNotes.push('Dein kurzer Schlaf senkt deinen Verbrauch leicht (ca. ' + sleepAdj + ' kcal/Tag), vor allem über weniger Alltagsbewegung. Mehr Schlaf ist einer der einfachsten Hebel.');
    // Stress: bewusst NICHT eingerechnet (Wirkung individuell zu unterschiedlich), nur Hinweis.
    // Vier Stufen (Entscheidung Sami): normal / occasional / often / high.
    if (input.stress === 'high') infoNotes.push('Du hast dauerhaft hohen Stress angegeben. Chronischer Stress kann Appetit und Verbrauch beeinflussen, aber sehr unterschiedlich je nach Person. Wir rechnen ihn bewusst nicht ein, um keine Scheingenauigkeit zu erzeugen. Achte auf deinen Gewichtsverlauf und nutze die Nachkalibrierung.');
    else if (input.stress === 'often') infoNotes.push('Du hast oft stressige Phasen angegeben. Stress kann Appetit, Schlaf und Alltagsbewegung beeinflussen. Wir rechnen ihn bewusst nicht fest ein, weil die Wirkung individuell sehr unterschiedlich ist. Beobachte deinen Gewichtsverlauf und nutze die Nachkalibrierung.');
    // Menstruationszyklus (Frauen): abgefragt und erklärt, aber als Hinweis, nicht eingerechnet.
    if (sex === 'f') {
      if (input.cyclePhase === 'luteal') infoNotes.push('In der zweiten Zyklushälfte (Lutealphase, nach dem Eisprung) steigt dein Grundumsatz um ca. 2 bis 8 %. Wir rechnen das nicht fest ein, weil es täglich schwankt. Rechne in dieser Phase mit etwas mehr Spielraum, dein echter Bedarf liegt dann leicht über dem angezeigten Wert.');
      else if (input.cyclePhase === 'follicular') infoNotes.push('In der ersten Zyklushälfte (Follikelphase, bis zum Eisprung) liegt dein Grundumsatz am unteren Rand deines Monatsschnitts. Der angezeigte Wert passt hier gut.');
      else infoNotes.push('Dein Kalorienbedarf schwankt über den Zyklus: in der zweiten Hälfte (nach dem Eisprung) ca. 2 bis 8 % höher. Wir fragen das ab, um dich einzuordnen, rechnen es aber nicht fest ein, weil es täglich schwankt.');
    }

    const allWarnings = [].concat(BMRres.warnings || [], plaus.warnings, supp.warnings, calib.warnings || []);
    if (foodFloored) allWarnings.push('Dein Essensbudget wurde auf deinen Grundumsatz (' + round(bmr) + ' kcal) angehoben. Darunter zu essen ist auf Dauer nicht sinnvoll: Wähle ein sanfteres Defizit oder weniger Alkohol.');
    // Alkohol-Warnung: wenn der Alkohol das Essensbudget unter das Pflicht-Minimum
    // aus Protein und Fett drueckt, bleibt fuer Kohlenhydrate nichts uebrig.
    if (alc.kcalPerDay > 0) {
      const proteinFatKcal = mac.protein_g * 4 + mac.fat_g * 9;
      if (foodTarget < proteinFatKcal) {
        allWarnings.push('Dein Alkoholkonsum (ca. ' + alc.kcalPerDay + ' kcal/Tag im Schnitt) frisst dein Essensbudget so stark auf, dass Protein und Fett nicht mehr sauber gedeckt sind. Weniger Alkohol würde deinen Fortschritt deutlich verbessern.');
      } else if (alc.kcalPerDay >= 150) {
        allWarnings.push('Alkohol bindet im Schnitt ca. ' + alc.kcalPerDay + ' kcal/Tag deines Budgets (ca. ' + alc.kcalPerWeek + ' kcal/Woche). Diese Kalorien liefern keine Nährstoffe.');
      }
    }

    const breakdown = [
      { key: 'bmr', label: 'Grundumsatz (BMR)', kcal: bmr, note: BMRres.label },
      { key: 'neat', label: 'Alltagsbewegung (NEAT)', kcal: NEATres.kcal, note: NEATres.source },
      { key: 'eat', label: 'Training (Durchschnitt/Tag)', kcal: round(eatWeek / 7), note: tDays + ' Trainingstage/Woche', hidden: tDays === 0 },
      { key: 'epoc', label: 'Nachbrenneffekt (EPOC)', kcal: round(epocWeek / 7), hidden: tDays === 0 },
      { key: 'tef', label: 'Nahrungsverwertung (TEF)', kcal: round((tefTrain * tDays + tefRest * restDays) / 7), note: tefSource },
      { key: 'at', label: 'Stoffwechsel-Anpassung (Diät)', kcal: at, hidden: at === 0 },
      { key: 'sleep', label: 'Schlaf-Korrektur', kcal: sleepAdj, hidden: sleepAdj === 0 },
      { key: 'caff', label: 'Koffein (Alltag)', kcal: baseCaff, hidden: baseCaff === 0 },
      { key: 'supp', label: 'HPN Support', kcal: supp.kcal, hidden: supp.kcal === 0 }
    ].filter(x => !x.hidden);

    return {
      bmr: BMRres,
      neat: NEATres,
      eat: { perTrainDay: round(eatTrainDay), epocPerTrainDay: round(epocTrainDay), blocks: blockDetails, trainingDays: tDays, restDays },
      tef: { train: tefTrain, rest: tefRest, source: tefSource },
      adaptiveThermo: at,
      supplement: supp,
      bmi: round(bmi, 1),
      tdee: { value: effectiveTDEE, estimated: tdee, source: tdeeSource, train: round(tdeeTrain), rest: round(tdeeRest), weeklyAvg: round(weeklyAvg) },
      calibration: calib,
      target: tgt,
      alcohol: alc,
      foodTarget: round(foodTarget), // Zielkalorien fuer Essen nach Alkohol-Abzug
      weeklyChangeKg: wk,
      macros: mac,
      warnings: allWarnings,
      infoNotes,
      breakdown
    };
  }

  const API = {
    calculate, computeBMR, computeNEAT, eatBlock, tefFromMacros, tefFromStatus,
    adaptiveThermo, alcoholBudget, adaptiveCalibration, sleepFactor, supplementThermo, caffeineKcal, targetCalories, macros, proteinGrams,
    navyBodyFat, weeklyChange, tissueEnergyDensity, plausibility,
    MET, EPOC_FACTOR, JOB_NEAT_80, ACT_NEAT_80, JOB_NONSTEP_80, FIDGET, MM, round
  };
  global.HPNCalc = API;
  if (typeof module !== 'undefined' && module.exports) module.exports = API;
})(typeof window !== 'undefined' ? window : globalThis);


/* Wissenschaftliche Quellen fuer die Fussnoten. Wird als window.HPN_SOURCES
   in das Ergebnis gerendert. Nur Anzeige, keine Rechenlogik.
   Nutzersichtbarer Text mit echten Umlauten. */
window.HPN_SOURCES = [
  { t: "Henry CJK (2005). Basal metabolic rate studies in humans, neue Gleichungen. Public Health Nutrition (Oxford/Henry).", u: "https://pubmed.ncbi.nlm.nih.gov/16277825/" },
  { t: "Cunningham JJ (1991). Body composition as a determinant of energy expenditure. Am J Clin Nutr.", u: "https://pubmed.ncbi.nlm.nih.gov/1985398/" },
  { t: "ten Haaf & Weijs (2014). Resting Energy Expenditure Prediction in Recreational Athletes. PLOS ONE.", u: "https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0108460" },
  { t: "Mifflin MD et al. (1990). A new predictive equation for resting energy expenditure.", u: "https://pubmed.ncbi.nlm.nih.gov/2305711/" },
  { t: "Pontzer H et al. (2021). Daily energy expenditure through the human life course. Science.", u: "https://www.science.org/doi/10.1126/science.abe5017" },
  { t: "2024 Adult Compendium of Physical Activities (MET-Werte).", u: "https://pmc.ncbi.nlm.nih.gov/articles/PMC10818145/" },
  { t: "Hall KD (2007). What is the required energy deficit per unit weight loss? Int J Obes.", u: "https://pmc.ncbi.nlm.nih.gov/articles/PMC2376744/" },
  { t: "TEF nach Makronährstoffen, Thermic effect of a meal (PMC).", u: "https://pmc.ncbi.nlm.nih.gov/articles/PMC3873760/" },
  { t: "Adaptive Thermogenese, Maintenance of Weight Loss. PLOS ONE.", u: "https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0004377" },
  { t: "Astrup A et al. (1990). Normal caffeine consumption: influence on thermogenesis. Am J Clin Nutr.", u: "https://pubmed.ncbi.nlm.nih.gov/2912010/" },
  { t: "Jäger R et al. (2017). ISSN Position Stand: protein and exercise (1,4 bis 2,0 g/kg für Aktive).", u: "https://pmc.ncbi.nlm.nih.gov/articles/PMC5477153/" },
  { t: "Morton RW et al. (2018). Meta-Analyse: Protein und Krafttraining, Optimum ca. 1,6 g/kg.", u: "https://pmc.ncbi.nlm.nih.gov/articles/PMC5867436/" },
  { t: "Helms ER et al. (2014). Protein im Kaloriendefizit bei Kraftsportlern (Muskelerhalt).", u: "https://pubmed.ncbi.nlm.nih.gov/24092765/" },
  { t: "Capsaicinoide und Thermogenese, Meta-Analyse (13 RCTs).", u: "https://onlinelibrary.wiley.com/doi/abs/10.1002/ptr.6897" },
  { t: "Paradieskörner (6-Paradol) aktiviert braunes Fettgewebe. Br J Nutr 2013.", u: "https://pubmed.ncbi.nlm.nih.gov/23308394/" },
  { t: "Muskelgedächtnis: epigenetisches Memory im Skelettmuskel.", u: "https://pubmed.ncbi.nlm.nih.gov/39570634/" },
  { t: "Constrained Total Energy Expenditure (2,5x BMR-Limit). Current Biology.", u: "https://pmc.ncbi.nlm.nih.gov/articles/PMC4803033/" },
  { t: "Stehen vs. Sitzen, Energieverbrauch Meta-Analyse (Saeidifard 2018).", u: "https://pubmed.ncbi.nlm.nih.gov/29385357/" },
  { t: "Tudor-Locke & Bassett (2004). How many steps/day are enough? Sports Med (Schrittkategorien).", u: "https://pubmed.ncbi.nlm.nih.gov/14715035/" },
  { t: "Suter PM et al. (1994). Effect of ethanol on energy expenditure (Alkohol-Thermogenese).", u: "https://pubmed.ncbi.nlm.nih.gov/8018665/" },
  { t: "US Navy / Hodgdon-Beckett Körperfett-Schätzung (Zirkumferenz-Methode).", u: "https://www.calculator.net/body-fat-calculator.html" }
];


/* ============================================================================
   HPN SCIENCE KALORIENRECHNER - UI Controller (Vanilla JS, framework-frei)
   Verdrahtet die Engine (window.HPNCalc) mit dem 7-Schritte-Wizard und dem
   Ergebnis. Kein Framework, keine externen Abhaengigkeiten, DOMContentLoaded-Guard.
   Die Rechenlogik liegt ausschliesslich in der Engine, hier passiert nur UI.
   ============================================================================ */
(function () {
  'use strict';
  function ready(fn) { if (document.readyState !== 'loading') fn(); else document.addEventListener('DOMContentLoaded', fn); }

  ready(function () {
    var root = document.querySelector('.hpncalc');
    if (!root || !window.HPNCalc) return;
    var C = window.HPNCalc;

    var TOTAL = 8;
    var state = {
      sex: 'm', age: null, cm: null, kg: null, bf: null, trained: false,
      steps: null, standingHours: 0, job: 'office', fidget: 'normal',
      blocks: [], proteinStatus: 'normal',
      weightLoss4wk: 0, sleepHours: null, alcoholPerWeek: 0, stress: 'normal', cyclePhase: 'unknown',
      muscleMemory: 'active',
      ignite: false, heat: false, extraCaffeineMg: 0, caffeineHabituation: 'none',
      goal: 'loss', goalOpt: { lossPct: 0.15 },
      calibration: null
    };
    var step = 0;

    var $ = function (s, c) { return (c || root).querySelector(s); };
    var $$ = function (s, c) { return Array.prototype.slice.call((c || root).querySelectorAll(s)); };
    function esc(x) { return String(x).replace(/[&<>"]/g, function (m) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[m]; }); }

    /* ---- Pill-/Karten-Auswahl ---- */
    function bindPills(sel, key, opts) {
      $$(sel).forEach(function (p) {
        p.addEventListener('click', function () {
          $$(sel).forEach(function (x) { x.classList.remove('sel', 'cy'); });
          p.classList.add('sel'); if (opts && opts.cy) p.classList.add('cy');
          var v = p.getAttribute('data-v');
          state[key] = (opts && opts.num) ? parseFloat(v) : v;
          if (opts && opts.cb) opts.cb(v);
        });
      });
    }

    /* ---- Trainingsbloecke (mit Einheiten pro Woche) ---- */
    var METKEYS = C.MET;
    function renderBlocks() {
      var wrap = $('#hc-blocks'); wrap.innerHTML = '';
      state.blocks.forEach(function (b, i) {
        var row = document.createElement('div'); row.className = 'hc-block';
        var optsHtml = Object.keys(METKEYS).map(function (k) {
          return '<option value="' + k + '"' + (b.type === k ? ' selected' : '') + '>' + esc(METKEYS[k].label) + '</option>';
        }).join('');
        row.innerHTML =
          '<div class="hc-field"><label>Art</label><select data-i="' + i + '" data-f="type">' + optsHtml + '</select></div>' +
          '<div class="hc-field"><label>Dauer je Einheit</label><div class="hc-unit" data-unit="min"><input type="number" min="5" max="300" value="' + (b.duration || 60) + '" data-i="' + i + '" data-f="duration" inputmode="numeric"></div></div>' +
          '<div class="hc-field"><label>Anstrengung <span class="hc-hint">4 = locker, 10 = alles gegeben</span></label><div class="hc-unit" data-unit="von 10"><input type="number" min="4" max="10" value="' + (b.rpe || 7) + '" data-i="' + i + '" data-f="rpe" inputmode="numeric"></div></div>' +
          '<div class="hc-field"><label>pro Woche</label><div class="hc-unit" data-unit="x"><input type="number" min="1" max="14" value="' + (b.perWeek || 3) + '" data-i="' + i + '" data-f="perWeek" inputmode="numeric"></div></div>' +
          '<button type="button" class="rm" data-rm="' + i + '" title="entfernen" aria-label="Block entfernen">&times;</button>';
        wrap.appendChild(row);
      });
      $$('#hc-blocks select,#hc-blocks input').forEach(function (el) {
        el.addEventListener('input', function () {
          var i = +el.getAttribute('data-i'), f = el.getAttribute('data-f');
          state.blocks[i][f] = (f === 'type') ? el.value : (+el.value);
          updateDaysInfo();
        });
      });
      $$('#hc-blocks .rm').forEach(function (b) {
        b.addEventListener('click', function () { state.blocks.splice(+b.getAttribute('data-rm'), 1); renderBlocks(); updateDaysInfo(); });
      });
      updateDaysInfo();
    }
    function maxPerWeek() {
      return state.blocks.reduce(function (m, b) { return Math.max(m, +b.perWeek || 0); }, 0);
    }
    function updateDaysInfo() {
      var el = $('#hc-daysinfo'); if (!el) return;
      if (!state.blocks.length) { el.textContent = 'Noch kein Training hinzugefügt. Ohne Training rechnen wir dich als Nicht-Sportler.'; return; }
      var td = Math.min(7, Math.round(maxPerWeek()));
      el.textContent = 'Daraus ergeben sich ' + td + ' Trainingstage pro Woche (' + (7 - td) + ' Ruhetage). Trainings- und Ruhetag werden getrennt gerechnet.';
    }
    $('#hc-addblock').addEventListener('click', function () {
      if (state.blocks.length >= 6) return;
      state.blocks.push({ type: 'strength', duration: 60, rpe: 7, perWeek: 3 }); renderBlocks();
    });

    /* ---- Toggles ---- */
    function bindToggle(swSel, key, parentSel, cb) {
      var sw = $(swSel); if (!sw) return;
      sw.addEventListener('click', function () {
        state[key] = !state[key];
        sw.classList.toggle('on', state[key]);
        if (parentSel) { var p = $(parentSel); if (p) p.classList.toggle('on', state[key]); }
        if (cb) cb();
      });
    }

    /* ---- KFA visuell: eigene parametrische Silhouetten (HPN-Stil) ----
       Gefuellte Minimal-Silhouetten, per Catmull-Rom-Spline aus Breiten-Stationen
       erzeugt (halbe Kontur, gespiegelt). Bewusst eigener Stil, keine Uebernahme
       fremder Grafiken. w = halbe Koerperbreite je Y-Station, cuts = Definitionslinien. */
    var KFA_Y_M = [24, 32, 44, 58, 74, 92, 108, 126, 144, 158];
    var KFA_Y_F = [24, 32, 44, 58, 72, 88, 104, 120, 142, 158];
    var KFA_TILES = {
      m: [
        { bf: 6,  r: '5–7%',   w: [7, 11, 34, 32, 27, 21, 22, 24, 22, 19], cuts: 3 },
        { bf: 10, r: '8–12%',  w: [7, 11, 33, 31, 27, 22, 23, 24, 22, 19], cuts: 3 },
        { bf: 15, r: '13–17%', w: [7, 11, 32, 30, 27, 24, 25, 25, 23, 20], cuts: 2 },
        { bf: 20, r: '18–22%', w: [7, 11, 31, 30, 27, 26, 27, 26, 23, 20], cuts: 1 },
        { bf: 25, r: '23–27%', w: [7, 11, 31, 30, 28, 28, 30, 27, 24, 21], cuts: 0 },
        { bf: 30, r: '28–33%', w: [7, 12, 31, 30, 29, 31, 33, 29, 25, 22], cuts: 0 },
        { bf: 36, r: '34–39%', w: [8, 12, 31, 31, 31, 34, 37, 31, 27, 23], cuts: 0 },
        { bf: 43, r: '40%+',   w: [8, 13, 32, 32, 33, 38, 41, 34, 29, 24], cuts: 0 }
      ],
      f: [
        { bf: 14, r: '12–16%', w: [6, 9, 25, 27, 20, 16, 20, 27, 24, 20], cuts: 1 },
        { bf: 19, r: '17–21%', w: [6, 9, 25, 27, 21, 17, 21, 28, 25, 21], cuts: 1 },
        { bf: 24, r: '22–26%', w: [6, 9, 25, 28, 22, 19, 23, 29, 26, 22], cuts: 0 },
        { bf: 29, r: '27–31%', w: [6, 10, 26, 28, 23, 21, 25, 30, 27, 23], cuts: 0 },
        { bf: 34, r: '32–36%', w: [6, 10, 26, 29, 25, 24, 28, 31, 28, 24], cuts: 0 },
        { bf: 39, r: '37–41%', w: [7, 10, 27, 30, 27, 27, 31, 33, 29, 25], cuts: 0 },
        { bf: 45, r: '42–48%', w: [7, 11, 28, 31, 30, 31, 35, 35, 31, 26], cuts: 0 },
        { bf: 52, r: '49%+',   w: [7, 11, 29, 33, 33, 35, 39, 37, 33, 27], cuts: 0 }
      ]
    };
    function kfaSpline(pts) {
      var d = 'M' + pts[0][0].toFixed(1) + ',' + pts[0][1].toFixed(1);
      for (var i = 0; i < pts.length - 1; i++) {
        var p0 = pts[Math.max(0, i - 1)], p1 = pts[i], p2 = pts[i + 1], p3 = pts[Math.min(pts.length - 1, i + 2)];
        var c1x = p1[0] + (p2[0] - p0[0]) / 6, c1y = p1[1] + (p2[1] - p0[1]) / 6;
        var c2x = p2[0] - (p3[0] - p1[0]) / 6, c2y = p2[1] - (p3[1] - p1[1]) / 6;
        d += ' C' + c1x.toFixed(1) + ',' + c1y.toFixed(1) + ' ' + c2x.toFixed(1) + ',' + c2y.toFixed(1) + ' ' + p2[0].toFixed(1) + ',' + p2[1].toFixed(1);
      }
      return d;
    }
    function kfaTorsoSVG(t, ys, female) {
      var pts = ys.map(function (y, i) { return [60 + t.w[i], y]; });
      var half = 'M60,24 L' + (60 + t.w[0]).toFixed(1) + ',24 ' + kfaSpline(pts).slice(1) + ' L60,158 Z';
      var det = '', st = ' stroke="#FFFFFF" stroke-width="2.2" fill="none" stroke-linecap="round" opacity=".8"';
      if (t.cuts >= 1) det += '<path d="M60,' + (female ? 66 : 63) + ' L60,' + (t.cuts >= 2 ? 100 : 94) + '"' + st + '></path>';
      if (t.cuts >= 2) {
        var k = t.w[5] * 0.55, rows = t.cuts >= 3 ? [76, 86, 95] : [76, 86];
        rows.forEach(function (y) { det += '<path d="M' + (60 - k).toFixed(1) + ',' + y + ' Q60,' + (y + 2) + ' ' + (60 + k).toFixed(1) + ',' + y + '"' + st + '></path>'; });
      }
      if (t.cuts >= 3 && !female) {
        var pk = t.w[2] * 0.5;
        det += '<path d="M' + (60 - pk).toFixed(1) + ',56 Q60,62 ' + (60 + pk).toFixed(1) + ',56"' + st + '></path>';
      }
      return '<svg viewBox="0 0 120 170" aria-hidden="true" focusable="false"><g fill="currentColor"><circle cx="60" cy="12.5" r="11"></circle><path d="' + half + '"></path><path d="' + half + '" transform="translate(120,0) scale(-1,1)"></path></g>' + det + '</svg>';
    }
    function renderKfaVisual() {
      var grid = $('#kfa-visual-grid'); if (!grid) return;
      var female = state.sex === 'f';
      var tiles = KFA_TILES[female ? 'f' : 'm'], ys = female ? KFA_Y_F : KFA_Y_M;
      grid.innerHTML = tiles.map(function (t) {
        return '<button type="button" class="hc-choice" data-bf="' + t.bf + '">' + kfaTorsoSVG(t, ys, female) + '<strong>' + t.r + '</strong></button>';
      }).join('');
      $$('#kfa-visual-grid .hc-choice').forEach(function (c) {
        c.addEventListener('click', function () {
          $$('#kfa-visual-grid .hc-choice').forEach(function (x) { x.classList.remove('sel'); }); c.classList.add('sel');
          applyBF(+c.getAttribute('data-bf'));
          var res = $('#kfa-visual-result'); res.textContent = 'Übernommen: ~' + c.getAttribute('data-bf') + ' % Körperfett'; res.style.display = 'block';
        });
      });
      var res = $('#kfa-visual-result'); if (res) res.style.display = 'none';
    }

    /* ---- KFA-Helfer ---- */
    function initKFA() {
      var btn = $('#hc-kfa-toggle'), panel = $('#hc-kfa-panel');
      if (btn) btn.addEventListener('click', function () { panel.classList.toggle('open'); });
      $$('.hc-kfa-tab').forEach(function (t) {
        t.addEventListener('click', function () {
          $$('.hc-kfa-tab').forEach(function (x) { x.classList.remove('sel'); }); t.classList.add('sel');
          $$('.hc-kfa-view').forEach(function (v) { v.classList.remove('sel'); });
          $('#kfa-' + t.getAttribute('data-tab')).classList.add('sel');
        });
      });
      $('#kfa-navy-calc').addEventListener('click', function () {
        var neck = +$('#kfa-neck').value, waist = +$('#kfa-waist').value, hip = +($('#kfa-hip').value || 0);
        var bf = C.navyBodyFat({ sex: state.sex, height: state.cm || +$('#in-cm').value, neck: neck, waist: waist, hip: state.sex === 'f' ? hip : null });
        var res = $('#kfa-navy-result');
        if (bf) { res.textContent = 'Geschätzter Körperfettanteil: ~' + bf + ' %'; res.style.display = 'block'; applyBF(bf); }
        else { res.textContent = 'Bitte gültige Maße eingeben (Taille muss größer als Hals sein' + (state.sex === 'f' ? ', Hüfte erforderlich' : '') + ', Größe in Schritt 1 eintragen).'; res.style.display = 'block'; }
      });
      renderKfaVisual();
    }
    function applyBF(bf) { state.bf = bf; var inp = $('#in-bf'); if (inp) inp.value = bf; }
    function syncSexUI() {
      var hipWrap = $('#kfa-hip-wrap'); if (hipWrap) hipWrap.style.display = state.sex === 'f' ? 'flex' : 'none';
      var cyc = $('#hc-cycle-wrap'); if (cyc) cyc.style.display = state.sex === 'f' ? 'flex' : 'none';
      renderKfaVisual();
    }

    /* ---- Rohwerte aus den Eingabefeldern in den State ziehen ----
       Leere Felder: die optionalen/pflicht-Zahlen werden null (Engine haertet ab
       bzw. Validierung greift), reine Zusatzfelder werden 0. */
    var NUMFIELDS = [
      ['#in-age', 'age'], ['#in-cm', 'cm'], ['#in-kg', 'kg'], ['#in-bf', 'bf'],
      ['#in-steps', 'steps'], ['#in-stand', 'standingHours'],
      ['#in-loss', 'weightLoss4wk'], ['#in-sleep', 'sleepHours'], ['#in-alc', 'alcoholPerWeek'],
      ['#in-caff', 'extraCaffeineMg']
    ];
    var NULL_WHEN_EMPTY = { age: 1, cm: 1, kg: 1, bf: 1, steps: 1, sleepHours: 1 };
    function syncInputs() {
      NUMFIELDS.forEach(function (pair) {
        var el = $(pair[0]); if (!el) return;
        var key = pair[1];
        var raw = el.value.trim().replace(',', '.');
        if (raw === '') { state[key] = NULL_WHEN_EMPTY[key] ? null : 0; return; }
        var v = parseFloat(raw);
        state[key] = isFinite(v) ? v : (NULL_WHEN_EMPTY[key] ? null : 0);
      });
    }

    /* ---- Pflicht-Validierung Schritt 1 (markt-ready: kein Unsinn-Ergebnis) ---- */
    function validateStep1() {
      syncInputs();
      var problems = [];
      function need(key, label, lo, hi) {
        var v = state[key];
        $(key === 'age' ? '#in-age' : key === 'cm' ? '#in-cm' : '#in-kg').classList.remove('invalid');
        if (v == null || !isFinite(v)) { problems.push(label + ' fehlt'); mark(key); }
        else if (v < lo || v > hi) { problems.push(label + ' sollte zwischen ' + lo + ' und ' + hi + ' liegen'); mark(key); }
      }
      function mark(key) { var el = $(key === 'age' ? '#in-age' : key === 'cm' ? '#in-cm' : '#in-kg'); if (el) el.classList.add('invalid'); }
      need('age', 'Alter', 14, 100);
      need('cm', 'Größe', 120, 230);
      need('kg', 'Gewicht', 35, 250);
      var bfEl = $('#in-bf'); if (bfEl) bfEl.classList.remove('invalid');
      if (state.bf != null && (state.bf < 3 || state.bf > 60)) { problems.push('Körperfett sollte zwischen 3 und 60 % liegen (oder leer bleiben)'); if (bfEl) bfEl.classList.add('invalid'); }
      var box = $('#err-1');
      if (problems.length) {
        box.textContent = 'Bitte prüfe deine Eingaben: ' + problems.join(', ') + '.';
        box.classList.add('show');
        return false;
      }
      box.classList.remove('show');
      return true;
    }

      /* ---- Navigation ---- */
    function show(n, doScroll) {
      step = Math.max(0, Math.min(TOTAL - 1, n));
      $$('.hc-step').forEach(function (s, i) { s.classList.toggle('active', i === step); });
      $('#hc-bar').style.width = ((step + 1) / TOTAL * 100) + '%';
      $('#hc-stepnum').textContent = 'Schritt ' + (step + 1) + ' von ' + TOTAL;
      $('#hc-back').disabled = step === 0;
      $('#hc-next').textContent = step === TOTAL - 1 ? 'Ergebnis berechnen →' : 'Weiter →';
      if (doScroll !== false) { var shell = root.querySelector('.hc-shell'); if (shell) shell.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
    }
    $('#hc-back').addEventListener('click', function () { show(step - 1); });
    $('#hc-next').addEventListener('click', function () {
      if (step === 0 && !validateStep1()) return;
      syncInputs();
      if (step === TOTAL - 1) { compute(); } else { show(step + 1); }
    });

    /* ---- Rechnen + Rendern ---- */
    function compute() {
      syncInputs();
      var r = C.calculate(state);
      renderResults(r);
      // Ergebnis ERSETZT den Wizard: Eingabe-Shell ausblenden, nur Ergebnis zeigen.
      var shell = root.querySelector('.hc-shell'); if (shell) shell.style.display = 'none';
      var out = $('#hc-results'); out.classList.add('show');
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    function renderResults(r) {
      var out = $('#hc-results');
      var goalLabels = { loss: 'Zielkalorien zum Abnehmen', hold: 'Erhaltungskalorien', muscle: 'Zielkalorien Muskelaufbau', bulk: 'Zielkalorien Massephase', recomp: 'Zielkalorien Recomposition' };
      var maxBar = Math.max.apply(null, r.breakdown.map(function (b) { return Math.abs(b.kcal); }).concat([1]));
      var html = '';

      // HERO
      html += '<div class="hc-hero-num"><div class="lbl">' + (goalLabels[state.goal] || 'Zielkalorien') + '</div>' +
        '<div class="big">' + r.target.target + '<small> kcal/Tag</small></div>' +
        '<div class="sub">' + esc(r.target.note) + (r.weeklyChangeKg !== 0 ? ' · ca. ' + (r.weeklyChangeKg > 0 ? '+' : '') + r.weeklyChangeKg + ' kg/Woche' : '') + '</div>' +
        '<div class="src">Verbrauch ' + esc(r.tdee.source) + ' · ' + r.tdee.value + ' kcal/Tag</div></div>';

      // Ein Tagesziel plus Verbrauch nebeneinander. Kein Carb-Cycling mehr in der
      // Anzeige (Entscheidung Sami 2026-07-04): der Wochenschnitt ist der belastbare
      // Wert, die freie Tagesverteilung wird unter den Makros als Hinweis erklaert.
      html += '<div class="hc-daysplit">' +
        '<div class="ds"><div class="v">' + r.target.target + '</div><div class="k">Ziel pro Tag</div></div>' +
        '<div class="ds"><div class="v">' + r.tdee.value + '</div><div class="k">Verbrauch pro Tag</div></div>' +
        '</div>';

      // Breakdown
      html += '<div class="hc-panel"><h4>Dein Kalorienverbrauch im Detail</h4>';
      html += '<p style="margin:-.6rem 0 1.4rem;color:var(--muted);font-size:.92rem">Gesamtumsatz (Wochenschnitt): <strong style="color:var(--ink)">' + r.tdee.value + ' kcal/Tag</strong></p>';
      r.breakdown.forEach(function (b) {
        var pct = Math.abs(b.kcal) / maxBar;
        // Fuellung, Hoehe und Farbe stehen bewusst als Inline-Style direkt am Element,
        // damit die Balken auch dann sichtbar sind, wenn die externe kalorienrechner.css
        // noch im Cache haengt oder nicht geladen ist. Farb-Variablen kommen aus .hpncalc.
        var grad = b.kcal < 0 ? 'linear-gradient(90deg,#ff9d6e,#f76d3c)' : 'linear-gradient(90deg,var(--cy),var(--cy-d))';
        html += '<div class="hc-bar' + (b.kcal < 0 ? ' neg' : '') + '"><div class="row"><span class="nm">' + esc(b.label) + (b.note ? '<small>' + esc(b.note) + '</small>' : '') + '</span>' +
          '<span class="vl">' + (b.kcal > 0 ? '+' : '') + b.kcal + ' kcal</span></div>' +
          '<div class="track"><div class="hc-fill" style="display:block;width:' + (pct * 100).toFixed(1) + '%;height:100%;border-radius:var(--r-full);background:' + grad + '"></div></div></div>';
      });
      html += '</div>';

      // Makros
      var m = r.macros;
      html += '<div class="hc-panel"><h4>Deine Makro-Empfehlung</h4>' +
        '<div class="hc-macros">' +
        '<div class="macro p"><div class="top" style="display:block"></div><div class="mv">' + m.protein_g + '<span style="font-size:.5em">g</span></div><div class="mk">Protein</div><div class="mg">' + m.protein_gkg + ' g/kg</div></div>' +
        '<div class="macro c"><div class="top" style="display:block"></div><div class="mv">' + m.carbs_g + '<span style="font-size:.5em">g</span></div><div class="mk">Kohlenhydrate</div><div class="mg">Rest-Kalorien</div></div>' +
        '<div class="macro f"><div class="top" style="display:block"></div><div class="mv">' + m.fat_g + '<span style="font-size:.5em">g</span></div><div class="mk">Fett</div><div class="mg">' + m.fat_gkg + ' g/kg</div></div>' +
        '</div>';
      html += '<p style="margin-top:1rem;color:var(--muted);font-size:.85rem;line-height:1.6"><strong style="color:var(--ink)">Das ist dein Tagesdurchschnitt.</strong> Wie du die Kohlenhydrate über die Woche verteilst, bleibt dir überlassen, solange die Wochenmenge stimmt. Isst du an einem Trainingstag etwa 20 Prozent mehr, iss an einem Ruhetag entsprechend weniger, dann bleibt deine Woche in Summe gleich. Ausrechnen musst du das nicht: Isst du jeden Tag gleich viel, nutzt dein Körper an Trainingstagen von selbst mehr davon, weil der Bedarf dort höher ist. Fürs Abnehmen macht die Verteilung keinen Unterschied, dafür zählt allein deine Wochenbilanz. Viele fühlen sich mit mehr Kohlenhydraten rund ums Training aber wacher und leistungsfähiger, und genau dafür lohnt sich das Verschieben.</p>';
      html += '<p style="margin-top:1rem;color:var(--muted);font-size:.85rem">Gegenprobe: ' + m.protein_g + 'g P + ' + m.carbs_g + 'g KH + ' + m.fat_g + 'g F = ' + m.check + ' kcal</p></div>';

      // Neu berechnen direkt nach der Makro-Empfehlung, vor Praezisions-Modus (Sami 2026-07-04).
      html += '<div class="hc-recalc"><button type="button" class="btn btn-primary" id="hc-restart">Neu berechnen</button></div>';

      // Praezisions-Modus + HPN-Support direkt unter den Makros (Sami: weniger Scrolltiefe, Funnel).
      html += calibHTML(r);
      html += suppResultHTML(r);

      // BMR-Transparenz
      var v = r.bmr.variants, vparts = [];
      if (v.oxford) vparts.push('Oxford/Henry ' + v.oxford);
      if (v.mifflin) vparts.push('Mifflin ' + v.mifflin);
      if (v.katch) vparts.push('Katch-McArdle ' + v.katch);
      if (v.tenHaaf) vparts.push('Ten-Haaf ' + v.tenHaaf);
      html += '<div class="hc-panel"><h4>Wie wir gerechnet haben</h4>' +
        '<p style="font-size:.92rem;color:var(--ink-2)">Für deinen Grundumsatz haben wir die <strong>' + esc(r.bmr.label) + '</strong>-Formel gewählt, da sie für dein Profil am genauesten ist' + (r.bmr.ageAdjust ? ' (inkl. Alterskorrektur ' + r.bmr.ageAdjust + ' kcal nach Pontzer 2021)' : '') + '.</p>' +
        '<p style="font-size:.85rem;color:var(--muted);margin-top:.6rem">Vergleich aller Formeln: ' + vparts.join(' · ') + ' kcal</p></div>';

      // Hinweise + Warnungen
      var ins = '';
      (r.infoNotes || []).forEach(function (n) { ins += insight('tip', null, n); });
      (r.warnings || []).forEach(function (w) { ins += insight('warn', null, w); });
      if (ins) html += '<div class="hc-panel"><h4>Deine persönlichen Hinweise</h4>' + ins + '</div>';

      // Quellen
      html += sourcesHTML();

      out.innerHTML = html;

      bindCalib();
      $('#hc-restart').addEventListener('click', function () {
        $('#hc-results').classList.remove('show');
        var shell = root.querySelector('.hc-shell'); if (shell) shell.style.display = '';
        state.calibration = null;
        show(0);
      });
    }

    function insight(type, title, txt) {
      return '<div class="hc-insight ' + type + '"><div>' + (title ? '<strong>' + esc(title) + '</strong> ' : '') + esc(txt) + '</div></div>';
    }

    /* ---- Praezisions-Modus: echte Tracking-Daten eingeben ---- */
    function calibHTML(r) {
      var active = r.calibration && r.calibration.ok;
      var c = state.calibration || {};
      return '<div class="hc-panel hc-calib"><span class="hc-badge">Präzisions-Modus</span>' +
        '<h4>Deine Zahl mit echten Daten schärfen</h4>' +
        '<p style="font-size:.92rem;color:var(--ink-2);margin-bottom:.4rem">Jede Formel schätzt. Wieg dich täglich morgens und tracke deine Kalorien. Nach 2 bis 4 Wochen trägst du hier deine <strong>Wochendurchschnitte</strong> ein, und wir rechnen deinen <strong>wahren</strong> Verbrauch rückwärts aus. Wochenschnitte statt einzelner Wiegetage gleichen Wasser-Schwankungen aus.</p>' +
        '<div class="hc-grid">' +
        '<div class="hc-field"><label>Ø Gewicht erste Woche <span class="hc-hint">Schnitt der ersten 7 Wiegetage</span></label><div class="hc-unit" data-unit="kg"><input type="number" id="cal-start" value="' + (c.startWeightKg || '') + '" step="0.1" inputmode="decimal"></div></div>' +
        '<div class="hc-field"><label>Ø Gewicht letzte Woche <span class="hc-hint">Schnitt der letzten 7 Wiegetage</span></label><div class="hc-unit" data-unit="kg"><input type="number" id="cal-cur" value="' + (c.currentWeightKg || '') + '" step="0.1" inputmode="decimal"></div></div>' +
        '<div class="hc-field"><label>Ø Kalorien pro Tag <span class="hc-hint">dein Schnitt im Zeitraum</span></label><div class="hc-unit" data-unit="kcal"><input type="number" id="cal-int" value="' + (c.avgIntakeKcal || '') + '" step="50" inputmode="numeric"></div></div>' +
        '<div class="hc-field"><label>Zeitraum gesamt <span class="hc-hint">Anzahl der Tage</span></label><div class="hc-unit" data-unit="Tage"><input type="number" id="cal-days" value="' + (c.days || '') + '" min="14" inputmode="numeric"></div></div>' +
        '</div>' +
        '<p style="font-size:.8rem;color:var(--muted);margin:.8rem 0 0">Tipp: Die erste Diätwoche verfälscht durch Wasser- und Glykogenverlust. Entweder 3 bis 4 Wochen tracken oder erst ab Woche 2 starten. Frauen: Ein Zeitraum von rund 28 Tagen vergleicht automatisch die gleiche Zyklusphase.</p>' +
        '<button type="button" class="btn btn-cy" id="cal-run" style="margin-top:1rem">Verbrauch aus meinen Daten berechnen</button>' +
        (active ? '<div class="hc-calib-active">Dein Ergebnis oben basiert jetzt auf deinen echten Daten (' + r.calibration.impliedTDEE + ' kcal/Tag, ' + r.calibration.days + ' Tage). Die Formel-Schätzung lag bei ' + r.tdee.estimated + ' kcal/Tag.</div>' : '') +
        '</div>';
    }
    function bindCalib() {
      var run = $('#cal-run'); if (!run) return;
      run.addEventListener('click', function () {
        var start = parseFloat(($('#cal-start').value || '').replace(',', '.'));
        var cur = parseFloat(($('#cal-cur').value || '').replace(',', '.'));
        var intake = parseFloat(($('#cal-int').value || '').replace(',', '.'));
        var days = parseFloat(($('#cal-days').value || '').replace(',', '.'));
        if (!isFinite(start) || !isFinite(cur) || !isFinite(intake) || !isFinite(days)) {
          state.calibration = null;
        } else {
          // Wochenschnitt-Modus: Endpunkte sind 7-Tage-Durchschnitte (siehe Engine)
          state.calibration = { startWeightKg: start, currentWeightKg: cur, avgIntakeKcal: intake, days: days, weeklyAverages: true };
        }
        compute();
      });
    }

    /* ---- HPN-Support-Block im Ergebnis (informativ, Auswahl war in Schritt 8) ---- */
    function suppResultHTML(r) {
      var supKcal = r.supplement.kcal;
      var chosen = state.heat || state.ignite;
      var headline = chosen
        ? 'H.P.N Metabolismus Booster ist in deinem Ergebnis enthalten'
        : 'H.P.N Metabolismus Booster (nicht aktiviert)';
      var body = chosen
        ? 'Du hast ' + [state.heat ? 'Heat' : null, state.ignite ? 'Ignite' : null].filter(Boolean).join(' und ') +
          ' angegeben. Der thermogene Effekt (+' + supKcal + ' kcal/Tag) ist oben bereits eingerechnet. Heat-Koffein ist mit deinem Alltagskonsum verrechnet, nichts wird doppelt gezählt.'
        : 'Du hast keine H.P.N Metabolismus-Booster angegeben. Heat und Ignite erhöhen den Energieverbrauch über thermogene Wirkstoffe – wenn du sie nimmst, kannst du sie im Rechner (Schritt 8) zuschalten.';
      // Optionales Produktbild rechts: URL kommt aus dem Section-Setting (Theme-Editor),
      // die Section schreibt sie nach window.HPNCALC_SUPP_IMG. Ohne Bild bleibt der Block
      // wie gehabt. Zeichen, die url() brechen koennten, werden entfernt.
      var suppImg = (typeof window !== 'undefined' && window.HPNCALC_SUPP_IMG) ? String(window.HPNCALC_SUPP_IMG).replace(/["'()<>]/g, '') : '';
      var imgTag = suppImg ? '<div class="hc-supp-img" aria-hidden="true" style="display:block;background-image:url(\'' + suppImg + '\')"></div>' : '';
      return '<div class="hc-supp' + (suppImg ? ' has-image' : '') + '">' +
        '<div class="hc-supp-body">' +
        '<div class="eyebrow">H.P.N Metabolismus Booster</div>' +
        '<h4>' + esc(headline) + '</h4>' +
        '<p>' + esc(body) + '</p>' +
        '<p class="supp-note">Die Berechnung ergibt sich nach aktueller Studienlage zu den enthaltenen Einzel-Wirkstoffen. Es gibt keine klinische Studie zur Gesamtformel. Individuelle Effekte können abweichen. Nahrungsergänzungsmittel sind kein Ersatz für eine ausgewogene Ernährung.</p>' +
        '<a class="supp-link" href="https://hpn-store.de/products/heat" target="_blank" rel="noopener">Heat &amp; Ignite ansehen →</a>' +
        '</div>' +
        imgTag +
        '</div>';
    }
    // Supplement-Toggles im Wizard (Schritt 8). Setzen nur den State, gerechnet wird beim Ergebnis.
    function bindWizardSupp() {
      ['heat', 'ignite'].forEach(function (k) {
        var sw = $('#sw-' + k), card = $('#st-' + k);
        if (!sw) return;
        sw.addEventListener('click', function () {
          state[k] = !state[k];
          sw.classList.toggle('on', state[k]);
          if (card) card.classList.toggle('on', state[k]);
        });
      });
    }

    /* ---- Quellen ---- */
    function sourcesHTML() {
      var S = window.HPN_SOURCES || [];
      var items = S.map(function (s, i) {
        return '<li id="src-' + (i + 1) + '">' + esc(s.t) + ' <a href="' + encodeURI(s.u) + '" target="_blank" rel="noopener">→</a></li>';
      }).join('');
      return '<div class="hc-sources"><h5>Wissenschaftliche Quellen</h5><ol>' + items + '</ol>' +
        '<div class="hc-disclaimer">Dieser Rechner liefert eine evidenzbasierte Schätzung. Selbst die besten Formeln haben eine individuelle Schwankung von ±10 bis 15%. Für maximale Präzision: dein Ergebnis nach 3 bis 4 Wochen im Präzisions-Modus mit echtem Tracking kalibrieren. Diese Berechnung ersetzt keine ärztliche oder ernährungsmedizinische Beratung.</div></div>';
    }

    /* ---- Bindungen ---- */
    bindPills('[data-pill="sex"]', 'sex', { cb: function () { syncSexUI(); } });
    bindPills('[data-pill="job"]', 'job');
    bindPills('[data-pill="fidget"]', 'fidget');
    bindPills('[data-pill="protein"]', 'proteinStatus');
    bindPills('[data-pill="stress"]', 'stress');
    bindPills('[data-pill="cycle"]', 'cyclePhase');
    bindPills('[data-pill="mm"]', 'muscleMemory');
    bindPills('[data-pill="caffhab"]', 'caffeineHabituation');
    $$('[data-pill="goal"]').forEach(function (c) {
      c.addEventListener('click', function () {
        $$('[data-pill="goal"]').forEach(function (x) { x.classList.remove('sel'); }); c.classList.add('sel');
        state.goal = c.getAttribute('data-v');
        var lo = $('#hc-loss-opt'); if (lo) lo.style.display = state.goal === 'loss' ? 'block' : 'none';
      });
    });
    bindPills('[data-pill="losspct"]', '_losspct', { num: true, cy: true, cb: function (v) { state.goalOpt = { lossPct: parseFloat(v) }; } });
    bindToggle('#sw-trained', 'trained', '#tg-trained');
    bindWizardSupp();

        renderBlocks(); initKFA(); syncSexUI(); show(0, false);
  });
})();

